import type {
  APIShareMine,
  APIShareUpdateRequest,
  ShareExpiry,
} from '@see-stella/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  authedGetMine,
  authedRegenerate,
  authedRevoke,
  authedUpdate,
  ShareApiError,
} from '../../lib/shareApi'
import { logError } from '../../lib/logger'

/** The four sections the owner can toggle. Lifestyle (wearable data) is off by default. */
export type Includes = {
  photos: boolean
  metrics: boolean
  routine: boolean
  lifestyle: boolean
}

/** Map a control-bar section to its `share.update` request field. */
const INCLUDE_FIELD: Record<keyof Includes, keyof APIShareUpdateRequest> = {
  photos: 'includePhotos',
  metrics: 'includeMetrics',
  routine: 'includeRoutine',
  lifestyle: 'includeLifestyle',
}

/** The include flags carried by a live link, as the local `Includes` shape. */
function includesOf(link: APIShareMine): Includes {
  return {
    photos: link.includePhotos,
    metrics: link.includeMetrics,
    routine: link.includeRoutine,
    lifestyle: link.includeLifestyle,
  }
}

export interface OwnerShare {
  includes: Includes
  /**
   * Toggle one section. Photos/Metrics are independent; the last-on section can't
   * be turned off. When a link is live, the change is written through to the SAME
   * link in place (no new token) — optimistic, then reconciled with the server.
   */
  setInclude(key: keyof Includes, on: boolean): void
  /** True while a share link is being minted. */
  creating: boolean
  /** A user-facing error from the last create/update/stop attempt, or null. */
  error: string | null
  /**
   * Mint a link-only share with the current sections + expiry, then open the OS
   * share sheet (best-effort). Resolves `true` when the link was minted, `false`
   * when the mint failed (in which case `error` is set) — never rejects, so the
   * caller can decide whether to close the sheet without an unhandled rejection.
   */
  create(expiry: ShareExpiry): Promise<boolean>
  /** The owner's active link, hydrated on mount + kept current, or null. */
  link: APIShareMine | null
  /** True while `revoke` is in flight (so the UI can disable Stop sharing). */
  stopping: boolean
  /**
   * True until the mount-time link hydration settles. The bar disables its toggles +
   * Share while set, so a user action can never race the initial `getMine`.
   */
  hydrating: boolean
  /** Hand the live link's URL to the OS share sheet (best-effort). No-op with no link. */
  share(): Promise<void>
  /** Stop sharing — revoke the active link. Clears `link` on success. */
  revoke(): Promise<void>
}

/**
 * Hand the URL to the OS share sheet, falling back to the clipboard when Web Share
 * is unavailable OR fails (e.g. an embedded WebView without Web Share, or the
 * transient-activation window lost after the mint's network round-trip). A user
 * cancelling the sheet (AbortError) counts as shared, not a silent copy. Best-effort:
 * never throws, so a share/copy hiccup can't fail an already-minted link. The robust
 * in-app path (a postMessage bridge to native Share) lands with Manage (plan 5.4).
 */
async function shareOrCopy(url: string): Promise<void> {
  if (typeof navigator === 'undefined') return
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ url })
      return
    } catch (e) {
      // AbortError = the user cancelled the sheet -> treat as shared, not a copy.
      // Read .name off DOMException too (it may not chain off Error in older or
      // embedded WebView engines).
      const name = e instanceof DOMException || e instanceof Error ? e.name : ''
      if (name === 'AbortError') return
      // otherwise fall through to the clipboard (unsupported target / lost activation)
    }
  }
  try {
    await navigator.clipboard?.writeText(url)
  } catch (e) {
    logError('share: clipboard copy failed', e)
  }
}

/**
 * Owner share state for the control bar + Create/Manage steps. Holds which sections
 * are shared, the active link (hydrated on mount from `getMine`), and the full owner
 * lifecycle: mint (`create`), in-place write-through of section toggles (`update`),
 * re-share (`share`), and stop (`revoke`). The Firebase token is requested per call
 * via the injected `requestToken` (the native handshake), so every call uses a
 * current token and a long owner session never hits an expired one.
 */
export function useOwnerShare(
  requestToken: () => Promise<string | null>,
  /**
   * Whether to hydrate the existing link on mount (a token round-trip). OwnerPage
   * passes `isHostedInApp()` so a plain-browser hit never triggers a handshake that
   * can't be answered. Defaults to true for standalone hook tests.
   */
  hydrate = true
): OwnerShare {
  const [includes, setIncludes] = useState<Includes>({
    photos: true,
    metrics: true,
    routine: false,
    // Wearable data is more sensitive than skin scores — off by default (§14).
    lifestyle: false,
  })
  const [link, setLink] = useState<APIShareMine | null>(null)
  const [creating, setCreating] = useState(false)
  const [stopping, setStopping] = useState(false)
  // True until the mount-time link hydration settles. The bar disables its toggles +
  // Share while this is set, so a user action can never race the initial `getMine`.
  const [hydrating, setHydrating] = useState(hydrate)
  const [error, setError] = useState<string | null>(null)

  // Refs mirror state so the stable `setInclude` callback reads current values
  // without re-binding (OwnerBar keeps a stable handler across renders).
  const includesRef = useRef(includes)
  includesRef.current = includes
  const linkRef = useRef(link)
  linkRef.current = link
  const stoppingRef = useRef(stopping)
  stoppingRef.current = stopping

  // `terminalSeq` counts TERMINAL actions (revoke) that invalidate any in-flight
  // `update`. An update captures it at dispatch and commits only if it is unchanged,
  // so a `revoke` (which bumps it) can never be undone by a late `update` response
  // resurrecting the link. Updates do NOT invalidate each other — they are serialized
  // through `updateChain` so their responses arrive in tap order and each success is a
  // strictly newer server truth than the last (the final one wins naturally).
  const terminalSeq = useRef(0)
  const updateChain = useRef<Promise<void>>(Promise.resolve())

  // Hydrate the owner's existing link on mount so the bar/sheet open in Manage state
  // and section toggles write through. The bar is disabled until this settles, so
  // there is no concurrent action to guard against here beyond unmount.
  useEffect(() => {
    if (!hydrate) return
    let live = true
    requestToken()
      .then(async (token) => {
        if (!token) return
        const existing = await authedGetMine(token)
        if (live && existing) {
          linkRef.current = existing
          setLink(existing)
          setIncludes(includesOf(existing))
        }
      })
      .catch((e) => logError('share: getMine failed', e))
      .finally(() => {
        if (live) setHydrating(false)
      })
    return () => {
      live = false
    }
  }, [requestToken, hydrate])

  // Write a section change through to the live link in place (no re-mint), then
  // reconcile local state with the returned link. Serialized (see `updateChain`), so
  // each success is the newest server truth and advances `linkRef` — the baseline a
  // later failed toggle reverts to. A `revoke` in between (bumps `terminalSeq`) drops
  // this commit so a stale update can't resurrect a stopped link.
  const writeThrough = useCallback(
    async (key: keyof Includes, on: boolean, term: number): Promise<void> => {
      // Already superseded by a revoke before this queued write even started — skip the
      // network call entirely (the backend `update` targets the user's ACTIVE link by
      // identity, so a stale PATCH must never be sent once the link is being stopped).
      if (term !== terminalSeq.current) return
      try {
        const token = await requestToken()
        // Re-check after the (async) token handshake — a revoke may have landed during
        // it; do NOT fire the PATCH in that case.
        if (term !== terminalSeq.current) return
        if (!token) throw new ShareApiError('unknown')
        const updated = await authedUpdate(token, { [INCLUDE_FIELD[key]]: on })
        if (term !== terminalSeq.current) return // revoked/superseded meanwhile
        linkRef.current = updated
        setLink(updated)
        setIncludes(includesOf(updated))
      } catch (e) {
        logError('share: update failed', e)
        if (term !== terminalSeq.current) return
        // Revert the optimistic toggle to the last CONFIRMED server state (advanced by
        // every prior serialized success, so this baseline is never stale).
        if (linkRef.current) setIncludes(includesOf(linkRef.current))
        setError('Could not update your share link. Please try again.')
      }
    },
    [requestToken]
  )

  const setInclude = useCallback(
    (key: keyof Includes, on: boolean) => {
      // Stop sharing is terminal — ignore toggles once a revoke is in flight so a
      // queued write can't outlive it. (The bar also disables toggles while stopping.)
      if (stoppingRef.current) return
      const next = { ...includesRef.current, [key]: on }
      // At least one section must stay shared — block the last one turning off (no
      // state change, no bogus write-through).
      if (!next.photos && !next.metrics && !next.routine && !next.lifestyle)
        return
      // Advance the ref synchronously so a second rapid toggle in the same tick layers
      // on top of this one (the state-synced ref only refreshes on render).
      includesRef.current = next
      setIncludes(next) // optimistic
      // When a link is live, persist the change to the SAME link in place. Clear any
      // stale update error on this fresh action, and serialize the write so parallel
      // toggles reach the server in tap order.
      if (linkRef.current) {
        setError(null)
        const term = terminalSeq.current
        updateChain.current = updateChain.current
          .catch(() => {})
          .then(() => writeThrough(key, on, term))
      }
    },
    [writeThrough]
  )

  const create = useCallback(
    async (expiry: ShareExpiry): Promise<boolean> => {
      setCreating(true)
      setError(null)
      try {
        // Let any in-flight write-through against a PRIOR link settle before minting a
        // new one. The backend `update` targets the user's active link by identity, so
        // a stale PATCH that landed after this mint would silently hit the NEW link.
        // Draining the chain here (updates are near-instant; empty in the common case)
        // guarantees the old link is the active one when that request lands.
        await updateChain.current.catch(() => {})
        const token = await requestToken()
        if (!token) throw new ShareApiError('unknown')
        const current = includesRef.current
        const { url, expiresAt } = await authedRegenerate(token, {
          includePhotos: current.photos,
          includeMetrics: current.metrics,
          includeRoutine: current.routine,
          includeLifestyle: current.lifestyle,
          expiry,
        })
        // A freshly minted link: 0 views, never opened. Set it (and the baseline) so
        // the sheet switches to Manage without a second round-trip. Create runs only
        // from the Create state (no link, sheet scrim over the bar), so no concurrent
        // toggle races it.
        const minted: APIShareMine = {
          url,
          includePhotos: current.photos,
          includeMetrics: current.metrics,
          includeRoutine: current.routine,
          includeLifestyle: current.lifestyle,
          expiresAt,
          lastViewedAt: null,
          viewCount: 0,
        }
        linkRef.current = minted
        setLink(minted)
        await shareOrCopy(url)
        return true
      } catch (e) {
        logError('share: create failed', e)
        setError('Could not create your share link. Please try again.')
        return false
      } finally {
        setCreating(false)
      }
    },
    [requestToken]
  )

  const share = useCallback(async (): Promise<void> => {
    const current = linkRef.current
    if (!current) return
    await shareOrCopy(current.url)
  }, [])

  const revoke = useCallback(async (): Promise<void> => {
    // Bump `terminalSeq` so any in-flight `update` is dropped on arrival — a stale
    // update can never resurrect the link we are about to stop.
    terminalSeq.current += 1
    setError(null)
    setStopping(true)
    try {
      const token = await requestToken()
      if (!token) throw new ShareApiError('unknown')
      await authedRevoke(token)
      linkRef.current = null
      setLink(null)
    } catch (e) {
      logError('share: revoke failed', e)
      setError('Could not stop sharing. Please try again.')
      // The link is STILL active (revoke failed), but the `terminalSeq` bump above
      // dropped the reconciliation of any in-flight toggle — so local `includes` may
      // now diverge from the server. Drain the update chain FIRST so any in-flight
      // PATCH has actually landed server-side, THEN re-fetch the authoritative link;
      // fetching before the PATCH settles would read pre-PATCH flags and re-diverge.
      try {
        await updateChain.current.catch(() => {})
        const token = await requestToken()
        if (token) {
          const fresh = await authedGetMine(token)
          linkRef.current = fresh
          setLink(fresh)
          if (fresh) setIncludes(includesOf(fresh))
        }
      } catch (reErr) {
        logError('share: revoke-recovery getMine failed', reErr)
      }
    } finally {
      setStopping(false)
    }
  }, [requestToken])

  return {
    includes,
    setInclude,
    creating,
    error,
    create,
    link,
    stopping,
    hydrating,
    share,
    revoke,
  }
}
